import { IonApp, IonRouterOutlet, IonSpinner, setupIonicReact } from "@ionic/react";
import { IonReactRouter } from "@ionic/react-router";
import { Redirect, Route } from "react-router-dom";
/* Core CSS required for Ionic components to work properly */
import "@ionic/react/css/core.css";

/* Basic CSS for apps built with Ionic */
import "@ionic/react/css/normalize.css";
import "@ionic/react/css/structure.css";
import "@ionic/react/css/typography.css";

/* Optional CSS utils that can be commented out */
import "@ionic/react/css/display.css";
import "@ionic/react/css/flex-utils.css";
import "@ionic/react/css/float-elements.css";
import "@ionic/react/css/padding.css";
import "@ionic/react/css/text-alignment.css";
import "@ionic/react/css/text-transformation.css";

/* Theme variables */
import { isPlatform } from "@ionic/core";
import { Capacitor } from "@capacitor/core";
import { Keyboard, KeyboardResize } from "@capacitor/keyboard";
import { SplashScreen } from "@capacitor/splash-screen";
import { StatusBar, Style } from "@capacitor/status-bar";
import React, { Suspense, useEffect } from "react";

/* Initialize i18n before any components render */
import { SurveyInstance } from "./components/Survey";
import { useAuth } from "./contexts/AuthContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import { SurveyContext } from "./contexts/SurveyContext";
import "./i18n";
import { setupInterceptors } from "./lib/axiosConfig";
import { setupPushNotifications } from "./lib/pushNotifcations";
import {
  CREATE_PASSWORD_URL,
  FORGOT_PASSWORD_URL,
  LOGIN_URL,
  REGISTER_URL,
  RESEND_INVITE_URL,
  RESET_PASSWORD_URL,
  VERIFY_EMAIL_URL,
} from "./utils/constant";
import "./theme/variables.css";
import "./pages/Home.css";

// Lazy-loaded route components
const CreatePassword = React.lazy(() => import("./pages/(auth)/CreatePassword"));
const ForgotPassword = React.lazy(() => import("./pages/(auth)/ForgotPassword"));
const Login = React.lazy(() => import("./pages/(auth)/Login"));
const Register = React.lazy(() => import("./pages/(auth)/Register"));
const ResendInvite = React.lazy(() => import("./pages/(auth)/ResendInvite"));
const ResetPassword = React.lazy(() => import("./pages/(auth)/ResetPassword"));
const VerifyEmail = React.lazy(() => import("./pages/(auth)/VerifyEmail"));
const Menu = React.lazy(() => import("./pages/Menu"));
const PrivacyPolicy = React.lazy(() => import("./pages/PrivacyPolicy").then(m => ({ default: m.PrivacyPolicy })));
const TermsOfUse = React.lazy(() => import("./pages/TermsOfUse").then(m => ({ default: m.TermsOfUse })));

setupIonicReact({
  mode: "ios",
});

const GlobalContext: React.FC<{ children: any }> = ({ children }) => {
  const [instance, setInstance] = React.useState<SurveyInstance>({
    questionAnswers: [],
    updates: [],
  });

  return (
    <SurveyContext.Provider
      value={{
        setInstance: setInstance,
        instance: instance,
      }}
    >
      {children}
    </SurveyContext.Provider>
  );
};

const App: React.FC = () => {
  const auth = useAuth();

  useEffect(() => {
    // Only re-setup interceptors when tokens change, not for any auth object mutation
    setupInterceptors(auth);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.tokens?.access_token]);

  useEffect(() => {
    setupPushNotifications();
  }, []);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    StatusBar.setStyle({ style: Style.Dark }); // white text on dark header
    Keyboard.setResizeMode({ mode: KeyboardResize.Body });
    SplashScreen.hide();
  }, []);

  const isDesktop = isPlatform("desktop");

  return (
    <IonApp>
      <LanguageProvider>
        <GlobalContext>
          <IonReactRouter>
            <Suspense fallback={<div className="ion-text-center ion-padding"><IonSpinner name="crescent" /></div>}>
            <IonRouterOutlet animated={isPlatform("capacitor") || !isDesktop}>
              <Route path={LOGIN_URL} component={Login} exact />
              <Route path={REGISTER_URL} component={Register} exact />
              <Route path={VERIFY_EMAIL_URL} component={VerifyEmail} exact />
              <Route
                path={FORGOT_PASSWORD_URL}
                component={ForgotPassword}
                exact
              />
              <Route path={RESET_PASSWORD_URL} component={ResetPassword} exact />
              <Route path={CREATE_PASSWORD_URL} component={CreatePassword} exact />
              <Route path={RESEND_INVITE_URL} component={ResendInvite} exact />
              <Route component={Menu} path="/app" />
              <Route exact path="/terms-of-use" component={TermsOfUse} />
              <Route exact path="/privacy-policy" component={PrivacyPolicy} />
              <Route exact path="/">
                <Redirect to={LOGIN_URL} />
              </Route>
            </IonRouterOutlet>
            </Suspense>
          </IonReactRouter>
        </GlobalContext>
      </LanguageProvider>
    </IonApp>
  );
};

export default App;
